"use client";

import { getProductsByTag } from "@/data/products";
import { SimpleProduct } from "@/types/product";
import ProductCard from "./cards/ProductCard";
import { useTranslations } from "next-intl";
import { useRTLAwareStyle } from "@/util/rtl";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { motion } from "framer-motion";

interface ProductListProps {
  tagName: string;
  lang: "en" | "ar";
  type:
    | "charming-bouquets"
    | "boxed-beauties"
    | "sweetheart-box"
    | "red-love"
    | "blooming-vases"
    | "acrylic-florals"
    | "national-day"
    | "christmas-box-arragement"
    | "christmas-bouquet"
    | "charming-blooms"
    | "valentines-bouquets"
    | "valentines-arrangements"
    | "bundle-bouquet"
    | "bundle-arrangement"
    | "hamper"
    | "ramadan-arrangements"
    | "ramadan-bouquets"
    | "womens-day-arrangements"
    | "womens-day-bouquets"
    | "mother-arrangement"
    | "mother-bouquet"
    | "eid-bouquet"
    | "eid-arrangement";
}

const ProductSkeleton = () => (
  <div className="bg-white shadow-md rounded-lg overflow-hidden">
    <div className="h-64 bg-gray-200 animate-pulse"></div>
    <div className="p-4">
      <div className="h-4 bg-gray-200 rounded w-3/4 mb-2 animate-pulse"></div>
      <div className="h-4 bg-gray-200 rounded w-1/2 animate-pulse"></div>
    </div>
  </div>
);

export default function ProductList({ tagName, lang, type }: ProductListProps) {
  const t = useTranslations("product");
  const rtlAwareStyle = useRTLAwareStyle(
    "space-x-4 rtl:space-x-reverse",
    "space-x-4",
  );
  const letterSpacing = useRTLAwareStyle("tracking-wider", "");

  const { data, isLoading, isError } = useQuery({
    queryKey: ["products", tagName, lang],
    queryFn: async () => getProductsByTag(tagName, lang),
  });

  // const sortedProducts = useMemo(() => {
  //   if (data && data.items) {
  //     return [...data.items].sort((a, b) => a.price - b.price)
  //   }
  //   return []
  // }, [data])

  const sortedProducts = useMemo(() => {
    if (data && data.items) {
      const regularProducts = data.items.filter(
        (product) => ![75, 78].includes(product.id),
      );
      const specialProducts = data.items.filter((product) =>
        [75, 78].includes(product.id),
      );

      // Sort regular products by price
      const sortedRegularProducts = regularProducts.sort(
        (a, b) => a.price - b.price,
      );

      // Combine sorted regular products with special products at the end
      return [...sortedRegularProducts, ...specialProducts];
    }
    return [];
  }, [data]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
    },
  };

  return (
    <div className="container mx-auto px-4 py-4 md:py-12">
      <div className="mb-12 w-full">
        <div className="relative py-4">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center">
            <span
              className={`bg-white px-6 text-2xl md:text-3xl font-semibold text-gray-900 font-fancy text-center ${letterSpacing}`}
            >
              {t(`heading.${type}`)}
            </span>
          </div>
        </div>
        <div className="mt-4 flex justify-center">
          <div className="w-24 h-1 bg-[#f0ede7] rounded-full"></div>
        </div>
      </div>
      {isError ? (
        <div className="text-center text-red-500">{t("error")}</div>
      ) : (
        <motion.div
          className="grid grid-cols-1 gap-6 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {isLoading ? (
            Array.from({ length: 6 }).map((_, index) => (
              <motion.div key={index} variants={itemVariants}>
                <ProductSkeleton />
              </motion.div>
            ))
          ) : sortedProducts.length > 0 ? (
            sortedProducts.map((product: SimpleProduct) => (
              <motion.div key={product.id} variants={itemVariants}>
                <ProductCard product={product} lang={lang} />
              </motion.div>
            ))
          ) : (
            <div className="col-span-full text-center text-gray-500">
              {t("noProducts")}
            </div>
          )}
        </motion.div>
      )}
    </div>
  );
}
